import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { http } from '../../api/client.js';
import { tx } from '../../i18n/translate.js';

interface HistoryRow {
  notifyId: string;
  businessFunction: string;
  subject: string;
  recipients: string[];
  successCount: number;
  failureCount: number;
  triggeredBy: string | null;
  occurredAt: string;
}

const FUNCTIONS = [
  { value: 'lead_created', label: 'Lead created' },
  { value: 'lead_created_overseas', label: 'Overseas lead created' },
  { value: 'lead_submitted', label: 'Lead approval submitted' },
  { value: 'lead_qualified', label: 'Lead qualified' },
  { value: 'lead_rejected', label: 'Lead rejected' },
  { value: 'account_submitted', label: 'Account approval submitted' },
  { value: 'account_approved', label: 'Account approved' },
  { value: 'account_rejected', label: 'Account rejected' },
  { value: 'opportunity_submitted', label: 'Opportunity approval submitted' },
  { value: 'opportunity_approved', label: 'Opportunity approved' },
  { value: 'opportunity_rejected', label: 'Opportunity rejected' },
  { value: 'opportunity_won', label: 'Opportunity won' },
  { value: 'opportunity_amount_over_1m_approved', label: 'High-value opportunity won' },
  { value: 'sinosure_submitted', label: 'Sinosure approval submitted' },
  { value: 'sinosure_approved', label: 'Sinosure approved' },
  { value: 'sinosure_rejected', label: 'Sinosure rejected' },
  { value: 'poc_approved', label: 'POC approved' },
  { value: 'poc_rejected', label: 'POC request rejected' },
];

interface SmtpHealth {
  ok: boolean;
  enabled: boolean;
  detail?: string;
}

interface SmtpConfig {
  id: string | null;
  configKey: string;
  category: 'qq' | 'microsoft' | 'aliyun';
  categoryLabel: string;
  fromName: string;
  fromEmail: string;
  replyTo: string | null;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  hasPassword: boolean;
  enabled: boolean;
  ready: boolean;
  lastVerifiedAt: string | null;
  lastVerifyStatus: 'untested' | 'success' | 'failed';
  lastVerifyMessage: string | null;
  modifiedOn: string | null;
}

interface NotifyTemplate {
  function: string;
  label: string;
  subject: string;
  body: string;
  enabled: boolean;
  modifiedOn: string | null;
}

interface OaNotificationRow {
  log_id: string | number;
  occurred_at: string;
  direction: 'outbound' | 'inbound';
  operation: string;
  entity_name: string | null;
  entity_id: string | null;
  approval_type: string | null;
  approval_title: string | null;
  http_status: number | null;
  duration_ms: number | null;
  result: 'success' | 'failure' | 'retrying';
  error_message: string | null;
  request_payload: unknown | null;
  response_payload: unknown | null;
}

interface CrestNotificationRow {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  kind: string;
  title: string;
  body: string | null;
  amount: number | null;
  result: string | null;
  targetEntity: string | null;
  targetId: string | null;
  occurredAt: string;
}

export function NotifyHistoryPage() {
  const [searchParams] = useSearchParams();
  const channel = searchParams.get('channel');

  if (channel === 'oa') return <OaNotificationPage />;
  if (channel === 'crest') return <CrestNotificationPage />;

  return <PlatformMailConfigPage />;
}

export function PlatformMailConfigPage() {
  return (
    <Card
      size="small"
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          CREST 邮箱配置
        </Typography.Title>
      }
    >
      <SmtpConfigPanel
        description="这里配置的是 CREST 平台自己的邮件发信通道，用于邮箱验证码登录等平台邮件。CRM 业务通知的模板、记录和发信配置请在销售中心维护。"
        successText="SMTP 验证通过，验证码登录将使用该邮箱发送"
      />
    </Card>
  );
}

export function CrmNotifyHistoryPage() {
  return (
    <Card
      size="small"
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          CRM 邮箱配置
        </Typography.Title>
      }
    >
      <Tabs
        defaultActiveKey="config"
        items={[
          {
            key: 'config',
            label: '邮箱配置',
            children: (
              <SmtpConfigPanel
                description="这里配置的是 CRM 业务通知的发信通道，用于客户、潜在客户、商机、信保等业务邮件。CREST 平台验证码邮箱请在系统设置中维护。"
                successText="SMTP 验证通过，CRM 业务通知将使用可用邮箱发送"
              />
            ),
          },
          {
            key: 'templates',
            label: '模板管理',
            children: <TemplateManagementPanel />,
          },
          {
            key: 'history',
            label: '发送日志',
            children: <NotifyHistoryRecords />,
          },
        ]}
      />
    </Card>
  );
}

function CrestNotificationPage() {
  const [rows, setRows] = useState<CrestNotificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<string | undefined>();
  const [user, setUser] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    http
      .get<{ rows: CrestNotificationRow[]; total: number }>('/notifications/admin', {
        params: { page, pageSize: 30, kind, user: user.trim() || undefined },
      })
      .then((r) => {
        setRows(r.data.rows);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [page, kind, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const columns: ColumnsType<CrestNotificationRow> = [
    {
      title: '时间',
      dataIndex: 'occurredAt',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '接收人',
      width: 220,
      render: (_v, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.userName || '—'}</Typography.Text>
          {row.userEmail && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.userEmail}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'kind',
      width: 130,
      render: (v: string) => <Tag color={v === 'approval' ? 'orange' : 'blue'}>{v}</Tag>,
    },
    {
      title: '标题',
      dataIndex: 'title',
      width: 260,
      ellipsis: true,
    },
    {
      title: '内容',
      dataIndex: 'body',
      ellipsis: true,
      render: (v: string | null) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 90,
      render: (v: string | null) =>
        v ? <Tag color={v === 'success' ? 'green' : 'red'}>{v}</Tag> : '—',
    },
    {
      title: '目标对象',
      width: 180,
      render: (_v, row) =>
        row.targetEntity && row.targetId ? (
          <Typography.Text>
            {row.targetEntity}/<Typography.Text code>{row.targetId.slice(0, 8)}</Typography.Text>
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card
        size="small"
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              CREST通知
            </Typography.Title>
            <Tag color="blue">共 {total} 条</Tag>
          </Space>
        }
        extra={
          <Space>
            <Input.Search
              allowClear
              placeholder="搜索接收人"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              onSearch={() => {
                setPage(1);
                refresh();
              }}
              style={{ width: 220 }}
            />
            <Select
              allowClear
              placeholder="消息类型"
              value={kind}
              onChange={(v) => {
                setKind(v);
                setPage(1);
              }}
              style={{ width: 150 }}
              options={[
                { label: 'account', value: 'account' },
                { label: 'opportunity', value: 'opportunity' },
                { label: 'lead', value: 'lead' },
                { label: 'sinosure', value: 'sinosure' },
                { label: 'sap', value: 'sap' },
              ]}
            />
            <Button onClick={refresh}>刷新</Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="此页面只展示 CREST 收到的站内通知消息"
          description="数据来源为 crm.app_notification，包含系统推送给用户并已持久化的 CREST 站内消息，不包含邮件发送历史和 OA 接口日志。"
        />
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize: 30,
            total,
            showTotal: (t) => `共 ${t} 条`,
            onChange: setPage,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </Spin>
  );
}

function OaNotificationPage() {
  const [rows, setRows] = useState<OaNotificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState<'outbound' | 'inbound' | undefined>();
  const [result, setResult] = useState<'success' | 'failure' | 'retrying' | undefined>();
  const [loading, setLoading] = useState(true);
  const [jsonPreview, setJsonPreview] = useState<OaNotificationRow | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    http
      .get<{ rows: OaNotificationRow[]; total: number }>('/integration/logs', {
        params: { page, pageSize: 30, system: 'oa', direction, result },
      })
      .then((r) => {
        setRows(r.data.rows);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [page, direction, result]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const columns: ColumnsType<OaNotificationRow> = [
    {
      title: '时间',
      dataIndex: 'occurred_at',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
    {
      title: '类型',
      dataIndex: 'direction',
      width: 110,
      render: (v: OaNotificationRow['direction']) => (
        <Tag color={v === 'outbound' ? 'geekblue' : 'cyan'}>
          {v === 'outbound' ? '发送给OA' : 'OA返回'}
        </Tag>
      ),
    },
    {
      title: '接口',
      dataIndex: 'operation',
      width: 180,
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      title: '审批类型',
      dataIndex: 'approval_type',
      width: 120,
      render: (v: string | null) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: '审批对象',
      dataIndex: 'approval_title',
      ellipsis: true,
      render: (v: string | null, row) =>
        v ||
        (row.entity_name && row.entity_id ? (
          <Typography.Text>
            {row.entity_name}/<Typography.Text code>{row.entity_id.slice(0, 8)}</Typography.Text>
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        )),
    },
    {
      title: 'HTTP',
      dataIndex: 'http_status',
      width: 90,
      render: (v: number | null) => v ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      width: 90,
      render: (v: number | null) => (v == null ? '—' : `${v}ms`),
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 90,
      render: (v: OaNotificationRow['result']) => {
        const color = v === 'success' ? 'green' : v === 'retrying' ? 'orange' : 'red';
        const label = v === 'success' ? '成功' : v === 'retrying' ? '重试中' : '失败';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: '错误',
      dataIndex: 'error_message',
      ellipsis: true,
      render: (v: string | null) =>
        v ? <Typography.Text type="danger">{v}</Typography.Text> : null,
    },
    {
      title: '消息 JSON',
      width: 110,
      fixed: 'right',
      render: (_v, row) => (
        <Button
          size="small"
          type="link"
          disabled={!row.request_payload}
          onClick={() => setJsonPreview(row)}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card
        size="small"
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              OA通知
            </Typography.Title>
            <Tag color="blue">共 {total} 条</Tag>
          </Space>
        }
        extra={
          <Space>
            <Select
              allowClear
              placeholder="通知方向"
              value={direction}
              onChange={(v) => {
                setDirection(v);
                setPage(1);
              }}
              style={{ width: 130 }}
              options={[
                { label: '发送给OA', value: 'outbound' },
                { label: 'OA返回', value: 'inbound' },
              ]}
            />
            <Select
              allowClear
              placeholder="结果"
              value={result}
              onChange={(v) => {
                setResult(v);
                setPage(1);
              }}
              style={{ width: 120 }}
              options={[
                { label: '成功', value: 'success' },
                { label: '失败', value: 'failure' },
                { label: '重试中', value: 'retrying' },
              ]}
            />
            <Button onClick={refresh}>刷新</Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="此页面只展示 OA 通知"
          description="包含 CREST 发送给 OA 的待办、转交、关闭、加签等接口记录，以及 OA 回调 CREST 后写入的返回记录。"
        />
        <Table
          rowKey="log_id"
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize: 30,
            total,
            showTotal: (t) => `共 ${t} 条`,
            onChange: setPage,
          }}
          scroll={{ x: 'max-content' }}
          expandable={{
            rowExpandable: (row) => Boolean(row.request_payload || row.response_payload),
            expandedRowRender: (row) => (
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <PayloadBlock title="请求 Payload" value={row.request_payload} />
                <PayloadBlock title="返回 Payload" value={row.response_payload} />
              </div>
            ),
          }}
        />
        <Modal
          open={Boolean(jsonPreview)}
          title="发送给 OA 的消息 JSON"
          footer={null}
          onCancel={() => setJsonPreview(null)}
          width={900}
        >
          {jsonPreview && (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="时间">
                  {new Date(jsonPreview.occurred_at).toLocaleString('zh-CN')}
                </Descriptions.Item>
                <Descriptions.Item label="接口">{jsonPreview.operation}</Descriptions.Item>
                <Descriptions.Item label="审批类型">
                  {jsonPreview.approval_type || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="审批对象">
                  {jsonPreview.approval_title || jsonPreview.entity_name || '—'}
                </Descriptions.Item>
              </Descriptions>
              <PayloadBlock title="请求 JSON" value={jsonPreview.request_payload} />
            </Space>
          )}
        </Modal>
      </Card>
    </Spin>
  );
}

function PayloadBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <div style={{ flex: 1, minWidth: 360 }}>
      <Typography.Text strong>{title}</Typography.Text>
      <pre
        style={{
          margin: '8px 0 0',
          padding: 12,
          maxHeight: 360,
          overflow: 'auto',
          background: '#0f172a',
          color: '#e5e7eb',
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {formatJsonPayload(value)}
      </pre>
    </div>
  );
}

function formatJsonPayload(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
}

function smtpCategoryDefaults(category: SmtpConfig['category']) {
  if (category === 'qq') {
    return {
      fromName: 'BaiChat',
      fromEmail: '2450216512@qq.com',
      replyTo: null,
      smtpHost: 'smtp.qq.com',
      smtpPort: 587,
      smtpSecure: false,
      smtpUser: '2450216512@qq.com',
    };
  }
  if (category === 'aliyun') {
    return {
      fromName: 'CREST',
      fromEmail: '',
      replyTo: null,
      smtpHost: 'smtp.aliyun.com',
      smtpPort: 587,
      smtpSecure: false,
      smtpUser: '',
    };
  }
  return {
    fromName: 'CREST',
    fromEmail: '',
    replyTo: null,
    smtpHost: 'smtp.office365.com',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
  };
}

function SmtpConfigPanel({
  description = '邮箱配置分为 QQ 邮箱、微软邮箱、阿里邮箱三类。只有启用且 SMTP 测试成功的邮箱，才会被系统用于发送邮件。',
  successText = 'SMTP 验证通过，邮箱配置已可用',
}: {
  description?: string;
  successText?: string;
}) {
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<SmtpConfig & { smtpPassword?: string }>();
  const [rows, setRows] = useState<SmtpConfig[]>([]);
  const [editing, setEditing] = useState<SmtpConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifyingKey, setVerifyingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const readyCount = rows.filter((row) => row.ready).length;

  const load = useCallback(() => {
    setLoading(true);
    http
      .get<SmtpConfig[]>('/admin/notify/smtp-configs')
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = (category: SmtpConfig['category'] = 'microsoft') => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      category,
      ...smtpCategoryDefaults(category),
      enabled: false,
      smtpPassword: undefined,
    } as SmtpConfig & { smtpPassword?: string });
    setOpen(true);
  };

  const openEdit = (row: SmtpConfig) => {
    setEditing(row);
    form.setFieldsValue({ ...row, smtpPassword: undefined });
    setOpen(true);
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        smtpPassword: values.smtpPassword?.trim() || undefined,
      };
      const path = editing
        ? `/admin/notify/smtp-configs/${encodeURIComponent(editing.configKey)}`
        : '/admin/notify/smtp-config';
      await http.post<SmtpConfig>(path, payload);
      message.success('邮箱配置已保存');
      setOpen(false);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? '邮箱配置保存失败');
    } finally {
      setSaving(false);
    }
  };

  const verify = async (row: SmtpConfig) => {
    setVerifyingKey(row.configKey);
    try {
      await http.post(`/admin/notify/smtp-configs/${encodeURIComponent(row.configKey)}/verify`);
      message.success(successText);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'SMTP 验证失败');
      load();
    } finally {
      setVerifyingKey(null);
    }
  };

  const remove = (row: SmtpConfig) => {
    modal.confirm({
      title: '删除邮箱配置',
      content: `确认删除 ${row.fromEmail || row.configKey}？删除后该邮箱不会再用于发送邮件。`,
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setDeletingKey(row.configKey);
        try {
          await http.delete(`/admin/notify/smtp-configs/${encodeURIComponent(row.configKey)}`);
          message.success('邮箱配置已删除');
          load();
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } } };
          message.error(err.response?.data?.message ?? '邮箱配置删除失败');
        } finally {
          setDeletingKey(null);
        }
      },
    });
  };

  const columns: ColumnsType<SmtpConfig> = [
    {
      title: '邮箱类别',
      dataIndex: 'categoryLabel',
      width: 120,
      render: (v: string, row) => (
        <Tag color={row.category === 'qq' ? 'green' : row.category === 'aliyun' ? 'orange' : 'blue'}>
          {v}
        </Tag>
      ),
    },
    {
      title: '发件邮箱',
      width: 260,
      render: (_v, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.fromEmail || '未配置'}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.fromName || 'CREST'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'SMTP 配置',
      width: 360,
      render: (_v, row) => (
        <Descriptions size="small" column={1} colon={false}>
          <Descriptions.Item label="Host">{row.smtpHost || '未配置'}</Descriptions.Item>
          <Descriptions.Item label="Port">
            {row.smtpPort} / {row.smtpSecure ? 'SSL/TLS' : 'STARTTLS'}
          </Descriptions.Item>
          <Descriptions.Item label="User">{row.smtpUser || '未配置'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      title: '可用状态',
      width: 150,
      render: (_v, row) => (
        <Space size={4} wrap>
          <Tag color={row.ready ? 'green' : row.enabled ? 'orange' : 'default'}>
            {row.ready ? '可选用' : row.enabled ? '待验证' : '未启用'}
          </Tag>
          {row.hasPassword ? <Tag color="blue">已存授权码</Tag> : <Tag>无授权码</Tag>}
        </Space>
      ),
    },
    {
      title: '最近验证',
      width: 220,
      render: (_v, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{row.lastVerifyStatus}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.lastVerifiedAt ? new Date(row.lastVerifiedAt).toLocaleString('zh-CN') : '未验证'}
          </Typography.Text>
          {row.lastVerifyMessage && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {row.lastVerifyMessage}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: '操作',
      width: 210,
      fixed: 'right',
      render: (_v, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Button size="small" loading={verifyingKey === row.configKey} onClick={() => verify(row)}>
            测试
          </Button>
          <Button
            danger
            size="small"
            loading={deletingKey === row.configKey}
            onClick={() => remove(row)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Alert
          type={readyCount > 0 ? 'success' : 'info'}
          showIcon
          message={readyCount > 0 ? `已有 ${readyCount} 个可用邮箱配置` : '暂无可用邮箱配置'}
          description={description}
        />
        <Card
          size="small"
          title="SMTP 邮箱配置池"
          extra={
            <Space>
              <Button onClick={() => openCreate('qq')}>新建 QQ 邮箱</Button>
              <Button onClick={() => openCreate('microsoft')}>新建微软邮箱</Button>
              <Button onClick={() => openCreate('aliyun')}>新建阿里邮箱</Button>
            </Space>
          }
        >
          <Table
            size="small"
            rowKey="configKey"
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </Space>

      <Modal
        open={open}
        title={editing ? '编辑邮箱配置' : '新建邮箱配置'}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setOpen(false)}
        width={760}
      >
        <Form form={form} layout="vertical" initialValues={{ smtpPort: 587, smtpSecure: false }}>
          <Space size={16} align="start" wrap style={{ width: '100%' }}>
            <Form.Item
              name="category"
              label="邮箱类别"
              style={{ width: 180 }}
              rules={[{ required: true, message: '请选择邮箱类别' }]}
            >
              <Select
                disabled={Boolean(editing)}
                onChange={(nextCategory: SmtpConfig['category']) => {
                  const defaults = smtpCategoryDefaults(nextCategory);
                  form.setFieldsValue({
                    ...defaults,
                    smtpPassword: undefined,
                  } as SmtpConfig & { smtpPassword?: string });
                }}
                options={[
                  { label: 'QQ邮箱', value: 'qq' },
                  { label: '微软邮箱', value: 'microsoft' },
                  { label: '阿里邮箱', value: 'aliyun' },
                ]}
              />
            </Form.Item>
            <Form.Item name="enabled" valuePropName="checked" label="启用">
              <Switch />
            </Form.Item>
            <Form.Item name="smtpSecure" valuePropName="checked" label="SSL/TLS">
              <Switch />
            </Form.Item>
          </Space>
          <Space size={16} align="start" wrap style={{ width: '100%' }}>
            <Form.Item
              name="fromName"
              label="发件人名称"
              style={{ minWidth: 240 }}
              rules={[{ required: true, message: '请输入发件人名称' }]}
            >
              <Input placeholder="CREST" />
            </Form.Item>
            <Form.Item
              name="fromEmail"
              label="发件邮箱"
              style={{ minWidth: 280 }}
              rules={[
                { required: true, message: '请输入发件邮箱' },
                { type: 'email', message: '请输入合法邮箱' },
              ]}
            >
              <Input placeholder="2450216512@qq.com" />
            </Form.Item>
            <Form.Item name="replyTo" label="Reply-To" style={{ minWidth: 280 }}>
              <Input placeholder="可选" />
            </Form.Item>
          </Space>
          <Space size={16} align="start" wrap style={{ width: '100%' }}>
            <Form.Item
              name="smtpHost"
              label="SMTP 主机"
              style={{ minWidth: 280 }}
              rules={[{ required: true, message: '请输入 SMTP 主机' }]}
            >
              <Input placeholder="smtp.qq.com" />
            </Form.Item>
            <Form.Item
              name="smtpPort"
              label="SMTP 端口"
              style={{ width: 140 }}
              rules={[{ required: true, message: '请输入端口' }]}
            >
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="smtpUser"
              label="SMTP 账号"
              style={{ minWidth: 260 }}
              rules={[{ required: true, message: '请输入 SMTP 账号' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="smtpPassword" label="SMTP 密码/授权码" style={{ minWidth: 260 }}>
              <Input.Password
                placeholder={
                  editing?.hasPassword
                    ? '已保存，留空则不修改'
                    : '请输入邮箱授权码，不是登录密码'
                }
              />
            </Form.Item>
          </Space>
          <Alert
            showIcon
            type="info"
            message="保存后请点击列表中的“测试”"
            description="测试成功后，该邮箱才会标记为“可选用”，系统后续会使用它发送邮件。"
          />
        </Form>
      </Modal>
    </Spin>
  );
}

function TemplateManagementPanel() {
  const { message } = App.useApp();
  const [rows, setRows] = useState<NotifyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<NotifyTemplate | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<NotifyTemplate>();

  const load = useCallback(() => {
    setLoading(true);
    http
      .get<NotifyTemplate[]>('/admin/notify/templates')
      .then((r) => setRows(r.data))
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { message?: string } } };
        message.error(err.response?.data?.message ?? '模板加载失败');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (row: NotifyTemplate) => {
    setEditing(row);
    form.setFieldsValue(row);
  };

  const save = async () => {
    if (!editing) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      await http.post(`/admin/notify/templates/${editing.function}`, values);
      message.success('模板已保存');
      setEditing(null);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? '模板保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Table
        loading={loading}
        rowKey="function"
        dataSource={rows}
        pagination={false}
        columns={[
          { title: '业务功能', dataIndex: 'label', width: 220 },
          {
            title: '代码',
            dataIndex: 'function',
            width: 240,
            render: (v) => <Typography.Text code>{v}</Typography.Text>,
          },
          { title: '模板主题', dataIndex: 'subject', ellipsis: true },
          {
            title: '状态',
            dataIndex: 'enabled',
            width: 100,
            render: (v) => <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '禁用'}</Tag>,
          },
          {
            title: '操作',
            width: 100,
            render: (_v, row) => (
              <Button type="link" onClick={() => openEdit(row)}>
                编辑
              </Button>
            ),
          },
        ]}
      />
      <Modal
        open={Boolean(editing)}
        title="编辑通知模板"
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="enabled" valuePropName="checked" label="启用模板">
            <Switch />
          </Form.Item>
          <Form.Item
            name="subject"
            label="邮件主题"
            rules={[{ required: true, message: '请输入邮件主题' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="body"
            label="邮件正文"
            rules={[{ required: true, message: '请输入邮件正文' }]}
          >
            <Input.TextArea rows={10} />
          </Form.Item>
          <Typography.Text type="secondary">
            可使用占位符：{'{{function}}'}、{'{{subject}}'}。后续可继续扩展业务字段变量。
          </Typography.Text>
        </Form>
      </Modal>
    </>
  );
}

function NotifyHistoryRecords() {
  const { message } = App.useApp();
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterFn, setFilterFn] = useState<string | undefined>();
  // P2-04: SMTP 健康 banner — 让用户知道通知是否真实发送 / 仅落库
  const [smtp, setSmtp] = useState<SmtpHealth | null>(null);
  useEffect(() => {
    http
      .get<SmtpHealth>('/admin/notify/smtp-health')
      .then((r) => setSmtp(r.data))
      .catch(() => setSmtp({ ok: false, enabled: false, detail: 'unreachable' }));
  }, []);
  // 测试发送
  const [testOpen, setTestOpen] = useState(false);
  const [testFn, setTestFn] = useState<string>('lead_created');
  const [testRecipient, setTestRecipient] = useState('');
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    http
      .get<{ rows: HistoryRow[]; total: number }>('/admin/notify/history', {
        params: { page, pageSize: 30, function: filterFn },
      })
      .then((r) => {
        setRows(r.data.rows);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [page, filterFn]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const testSend = async () => {
    setTesting(true);
    try {
      await http.post('/admin/notify/test-send', {
        function: testFn,
        recipient: testRecipient || undefined,
      });
      message.success(tx('legacy:pages.admin.notifyhistorypage.e86ec937'));
      setTestOpen(false);
      setTestRecipient('');
      refresh();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        err.response?.data?.message ?? tx('hardcoded:pages.admin.notifyhistorypage.c9da7c7c'),
      );
    } finally {
      setTesting(false);
    }
  };

  const columns: ColumnsType<HistoryRow> = [
    {
      title: tx('legacy:pages.admin.notifyhistorypage.c3a6c0a7'),
      dataIndex: 'occurredAt',
      width: 170,
      render: (v: string) => (
        <Typography.Text style={{ fontSize: 12 }}>
          {new Date(v).toLocaleString('zh-CN')}
        </Typography.Text>
      ),
    },
    {
      title: tx('hardcoded:pages.admin.notifyhistorypage.c34c6ae4'),
      dataIndex: 'businessFunction',
      width: 220,
      render: (v: string) => {
        const meta = FUNCTIONS.find((f) => f.value === v);
        return (
          <Space size={4}>
            <Typography.Text strong style={{ color: '#722ed1' }}>
              {meta?.label ?? v}
            </Typography.Text>
            <Typography.Text code style={{ fontSize: 10 }} type="secondary">
              {v}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: tx('hardcoded:pages.admin.notifyhistorypage.f00b33af'),
      dataIndex: 'subject',
      ellipsis: true,
    },
    {
      title: tx('hardcoded:pages.admin.notifyhistorypage.47cf6374'),
      dataIndex: 'recipients',
      width: 200,
      render: (v: string[]) =>
        v.length === 0 ? (
          <Typography.Text type="secondary">
            {tx('hardcoded:pages.admin.notifyhistorypage.88da907b')}
          </Typography.Text>
        ) : (
          <Space size={2} wrap>
            {v.slice(0, 2).map((r) => (
              <Tag key={r} color="blue" style={{ fontSize: 11 }}>
                {r}
              </Tag>
            ))}
            {v.length > 2 && <Tag>+{v.length - 2}</Tag>}
          </Space>
        ),
    },
    {
      title: tx('hardcoded:pages.admin.notifyhistorypage.d43f2815'),
      dataIndex: 'successCount',
      width: 80,
      align: 'right',
      render: (v: number) =>
        v > 0 ? (
          <Tag color="green">{v}</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: tx('hardcoded:pages.admin.notifyhistorypage.569c721d'),
      dataIndex: 'failureCount',
      width: 80,
      align: 'right',
      render: (v: number) =>
        v > 0 ? <Tag color="red">{v}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
    },
  ];

  return (
    <Spin spinning={loading}>
      <Card
        size="small"
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              通知记录
            </Typography.Title>
            <Tag color="blue">
              {total} {tx('hardcoded:pages.admin.notifyhistorypage.3158a551')}
            </Tag>
          </Space>
        }
        extra={
          <Space>
            <Button onClick={refresh}>
              {tx('hardcoded:pages.admin.notifyhistorypage.21fc280a')}
            </Button>
            <Button type="primary" onClick={() => setTestOpen(true)}>
              {tx('legacy:pages.admin.notifyhistorypage.5c4f7674')}
            </Button>
          </Space>
        }
      >
        {/* P2-04: SMTP 状态 banner — 让用户知道通知历史是真实发送 / 仅日志 */}
        {smtp && !smtp.enabled && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={tx('hardcoded:pages.admin.notifyhistorypage.smtp_disabled_title')}
            description={tx('hardcoded:pages.admin.notifyhistorypage.smtp_disabled_desc')}
          />
        )}
        {smtp?.enabled && !smtp.ok && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message={tx('hardcoded:pages.admin.notifyhistorypage.smtp_unhealthy_title')}
            description={
              smtp.detail ?? tx('hardcoded:pages.admin.notifyhistorypage.smtp_unhealthy_desc')
            }
          />
        )}
        {smtp?.enabled && smtp.ok && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 12 }}
            message={tx('hardcoded:pages.admin.notifyhistorypage.smtp_ok_title')}
          />
        )}
        <Typography.Paragraph type="secondary">
          {tx('hardcoded:pages.admin.notifyhistorypage.9aeab22f')}
        </Typography.Paragraph>
        <Space style={{ marginBottom: 12 }}>
          <Typography.Text>
            {tx('hardcoded:pages.admin.notifyhistorypage.90c09034')}
          </Typography.Text>
          <Select
            allowClear
            placeholder={tx('hardcoded:pages.admin.notifyhistorypage.8a133be4')}
            value={filterFn}
            onChange={(v) => {
              setFilterFn(v);
              setPage(1);
            }}
            style={{ width: 280 }}
            options={FUNCTIONS}
          />
        </Space>
        <Table
          rowKey="notifyId"
          columns={columns}
          dataSource={rows}
          size="middle"
          pagination={{
            current: page,
            pageSize: 30,
            total,
            showTotal: (t) => tx('hardcoded:pages.admin.notifyhistorypage.bce9502b', { v0: t }),
            onChange: setPage,
          }}
        />
      </Card>

      <Modal
        open={testOpen}
        title={tx('legacy:pages.admin.notifyhistorypage.5c4f7674')}
        okText={tx('legacy:pages.admin.notifyhistorypage.fdef161a')}
        cancelText={tx('hardcoded:pages.admin.notifyhistorypage.a6b97298')}
        onCancel={() => setTestOpen(false)}
        onOk={testSend}
        confirmLoading={testing}
      >
        <Typography.Paragraph type="secondary">
          {tx('hardcoded:pages.admin.notifyhistorypage.08b75843')}
        </Typography.Paragraph>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Typography.Text>
              {tx('hardcoded:pages.admin.notifyhistorypage.f8304a0f')}
            </Typography.Text>
            <Select
              value={testFn}
              onChange={setTestFn}
              options={FUNCTIONS}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <Typography.Text>
              {tx('hardcoded:pages.admin.notifyhistorypage.4adf2228')}
            </Typography.Text>
            <Input
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              placeholder="test@example.com"
              type="email"
            />
          </div>
        </Space>
      </Modal>
    </Spin>
  );
}
